import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const retailSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Retail Cleaning Services',
  description: 'Professional retail store cleaning services in Louisville, KY. Sales floor maintenance, storefront window cleaning, checkout sanitization, and after-hours service.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'First Insight Cleaning',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Louisville',
      addressRegion: 'KY',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Louisville' },
  serviceType: 'Retail Cleaning',
};

export default function RetailCleaningPage() {
  const [scrolled, setScrolled] = useState(true);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 10); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'ri-store-3-line',
      title: 'Sales Floor Maintenance',
      description: 'Keep your retail space pristine with regular cleaning of sales floors, aisles, and product displays to create an inviting shopping experience.'
    },
    {
      icon: 'ri-window-2-line',
      title: 'Window & Glass Cleaning',
      description: 'Crystal-clear storefront windows and glass displays that attract customers and showcase your products in the best light.'
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'Checkout Area Sanitization',
      description: 'High-frequency cleaning and disinfection of checkout counters, payment terminals, and customer touchpoints for safety.'
    },
    {
      icon: 'ri-door-open-line',
      title: 'Entrance & Vestibule Care',
      description: 'Maintain a welcoming first impression with spotless entrances, door handles, and vestibule areas cleaned multiple times daily.'
    },
    {
      icon: 'ri-shirt-line',
      title: 'Fitting Room Cleaning',
      description: 'Regular sanitization of fitting rooms, mirrors, and seating areas to ensure customer comfort and hygiene.'
    },
    {
      icon: 'ri-time-line',
      title: 'After-Hours Service',
      description: 'Flexible scheduling with after-hours and early morning cleaning to avoid disrupting your business operations and customer flow.'
    }
  ];

  const benefits = [
    'Customized cleaning schedules around your store hours',
    'Enhanced customer experience through cleanliness',
    'Reduced liability with proper floor care and maintenance',
    'Seasonal deep cleaning services available',
    'Emergency cleaning response for unexpected situations',
    'Retail-specific training for our cleaning teams'
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <SEOHead
        title="Retail Cleaning Services Louisville KY | First Insight Cleaning"
        description="Expert retail store cleaning in Louisville, KY. Sales floor maintenance, storefront window cleaning, checkout sanitization &amp; after-hours service. Enhance your customer experience. Free quote available."
        keywords="retail cleaning Louisville KY, store cleaning services Kentucky, commercial retail janitorial, First Insight Cleaning"
        canonical={`${siteUrl}/services/retail-cleaning`}
        schemaJson={retailSchema}
      />
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c1151d32743010c79d42ec9ed68620f1.jpg"
            alt="Professional Retail Cleaning Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/60 to-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Poppins']">
            Retail Cleaning Services
          </h1>
          <p className="text-base md:text-xl text-slate-100 mb-8 leading-relaxed">
            Create an inviting shopping environment that keeps customers coming back. Professional retail cleaning services designed to enhance your brand image and customer experience.
          </p>
          <Link
            to="/#quote"
            className="inline-block bg-teal-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Introduction */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-6 font-['Poppins']">
              Elevate Your Retail Space with Professional Cleaning
            </h2>
            <div className="w-16 h-1 bg-teal-500 mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  In the competitive retail industry, cleanliness directly impacts customer perception, shopping experience, and ultimately your bottom line. A spotless store creates trust, encourages longer visits, and increases the likelihood of purchases and return visits.
                </p>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Our retail cleaning services are specifically designed to meet the unique challenges of retail environments. We understand the importance of maintaining a pristine appearance during business hours while providing deep cleaning services when your store is closed.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  From boutique shops to large retail chains, we deliver consistent, high-quality cleaning that protects your investment in fixtures, flooring, and displays while creating an environment where customers feel comfortable and confident shopping.
                </p>
              </div>
              <div className="w-full h-[400px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_6db209a5a1f0a95f3ba88866dd1d4fe0_3187cae1.jpg"
                  alt="Retail Cleaning in Action"
                  className="w-full h-full object-cover object-center rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-slate-800 mb-12 text-center font-['Poppins']">
              Comprehensive Retail Cleaning Solutions
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-slate-100"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-500 rounded-lg mb-4">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-3">
                    {feature.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-teal-50 rounded-2xl p-12">
            <h3 className="text-3xl font-bold text-slate-800 mb-8 text-center font-['Poppins']">
              Benefits of Our Retail Cleaning Service
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-2xl text-teal-500"></i>
                  </div>
                  <p className="text-slate-700 text-lg">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h3 className="text-3xl font-bold text-slate-800 mb-6 font-['Poppins']">
              Ready to Enhance Your Customer Experience?
            </h3>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Let us help you create a shopping environment that reflects the quality of your brand and keeps customers coming back.
            </p>
            <Link
              to="/#quote"
              className="inline-block bg-teal-500 text-white px-10 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer text-lg"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
